import { doc as firestoreDoc, getDoc, setDoc, updateDoc, collection, addDoc, getDocs, orderBy, query, deleteDoc } from 'firebase/firestore';
import { db, formatDocument } from './firebase';
import { addService } from './services';

/**
 * Busca todos os salões cadastrados no sistema
 * @returns {Promise<Array>} - Lista de salões
 */
export const getAllSalons = async () => {
  try {
    const salonsRef = collection(db, 'salons');
    const q = query(salonsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => formatDocument(doc));
  } catch (error) {
    console.error('Erro ao buscar todos os salões:', error);
    throw error;
  }
};

/**
 * Busca a configuração de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<object|null>} - Dados do salão ou null se não encontrado
 */
export const getSalonConfig = async (salonId) => {
  try {
    const docRef = firestoreDoc(db, 'salons', salonId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return formatDocument(docSnap);
    } else {
      console.error('Salão não encontrado');
      return null;
    }
  } catch (error) {
    console.error('Erro ao buscar configuração do salão:', error);
    throw error;
  }
};

/**
 * Atualiza ou cria a configuração de um salão
 * @param {string} salonId - ID do salão
 * @param {object} config - Configurações do salão
 * @returns {Promise<boolean>} - True se bem-sucedido
 */
export const updateSalonConfig = async (salonId, config) => {
  try {
    const docRef = firestoreDoc(db, 'salons', salonId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      // Atualizar documento existente
      await updateDoc(docRef, {
        ...config,
        updatedAt: new Date()
      });
    } else {
      // Criar novo documento
      await setDoc(docRef, {
        id: salonId,
        ...config,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    return true;
  } catch (error) {
    console.error('Erro ao atualizar configuração do salão:', error);
    throw error;
  }
};

/**
 * Função para criar um salão de exemplo para demonstração
 * @returns {Promise<string>} - ID do salão criado
 */
export const createExampleSalon = async () => {
  const salonId = 'exemplo-salao';
  
  try {
    // Verificar se o salão de exemplo já existe
    const salonRef = firestoreDoc(db, 'salons', salonId);
    const salonSnap = await getDoc(salonRef);
    
    if (!salonSnap.exists()) {
      // Criar configuração do salão
      await setDoc(salonRef, {
        id: salonId,
        name: 'Salão Exemplo',
        logoUrl: 'https://via.placeholder.com/150',
        // Informações de contato
        phone: '(13) 3041-7402',
        email: 'contato@salaoexemplo.com.br',
        // Endereço completo
        cep: '11045-003',
        street: 'Av. Conselheiro Nébias',
        number: '754',
        complement: '',
        neighborhood: 'Boqueirão',
        city: 'Santos',
        state: 'SP',
        // Endereço formatado para exibição
        address: 'Av. Conselheiro Nébias, 754, Boqueirão - Santos - SP',
        fontFamily: "'Poppins', sans-serif",
        primaryColor: '#000000',
        headerStyle: 'text-only',
        maxPerSlot: 3,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Criar serviços de exemplo
      const servicesRef = collection(db, `salons/${salonId}/services`);
      const services = [
        { 
          nome: 'Corte Feminino', 
          preco: 70, 
          duration: 60, 
          setupTime: 5,
          finishTime: 5,
          allowsOverlap: false,
          createdAt: new Date() 
        },
        { 
          nome: 'Corte Masculino', 
          preco: 50, 
          duration: 30, 
          setupTime: 5,
          finishTime: 5,
          allowsOverlap: false,
          createdAt: new Date() 
        },
        { 
          nome: 'Coloração', 
          preco: 120, 
          duration: 120, 
          setupTime: 10,
          finishTime: 15,
          allowsOverlap: true,
          createdAt: new Date() 
        },
        { 
          nome: 'Hidratação', 
          preco: 80, 
          duration: 60, 
          setupTime: 5,
          finishTime: 5,
          allowsOverlap: true,
          createdAt: new Date() 
        },
        { 
          nome: 'Manicure', 
          preco: 40, 
          duration: 45, 
          setupTime: 5,
          finishTime: 5,
          allowsOverlap: true,
          createdAt: new Date() 
        },
        { 
          nome: 'Pedicure', 
          preco: 45, 
          duration: 45, 
          setupTime: 5,
          finishTime: 5,
          allowsOverlap: true,
          createdAt: new Date() 
        }
      ];
      
      for (const service of services) {
        await addDoc(servicesRef, service);
      }
      
      // Criar profissionais de exemplo
      const professionalsRef = collection(db, `salons/${salonId}/professionals`);
      const professionals = [
        { nome: 'Ana Silva', createdAt: new Date() },
        { nome: 'Carlos Oliveira', createdAt: new Date() },
        { nome: 'Juliana Santos', createdAt: new Date() }
      ];
      
      for (const professional of professionals) {
        await addDoc(professionalsRef, professional);
      }
      
      console.log('Salão de exemplo criado com sucesso!');
    }
    
    return salonId;
  } catch (error) {
    console.error('Erro ao criar salão de exemplo:', error);
    throw error;
  }
};

/**
 * Busca categorias de um salão
 * @param {string} salonId - ID do salão
 * @returns {Promise<Array>} - Lista de categorias
 */
export const getCategories = async (salonId) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  try {
    const categoriesRef = collection(db, `salons/${salonId}/categories`);
    const categoriesSnapshot = await getDocs(query(categoriesRef, orderBy('ordem')));
    return categoriesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Erro ao buscar categorias:', error);
    throw error;
  }
};

/**
 * Adiciona uma nova categoria
 * @param {string} salonId - ID do salão
 * @param {object} categoryData - Dados da categoria
 * @returns {Promise<object>} - Categoria criada com ID
 */
export const addCategory = async (salonId, categoryData) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!categoryData || !categoryData.nome) {
    throw new Error('Nome da categoria é obrigatório');
  }
  
  try {
    // Garantir que ordem seja um número válido
    const sanitizedData = {
      ...categoryData,
      ordem: typeof categoryData.ordem === 'number' ? categoryData.ordem : 0,
      createdAt: new Date()
    };
    
    const categoriesRef = collection(db, `salons/${salonId}/categories`);
    const docRef = await addDoc(categoriesRef, sanitizedData);
    return { 
      id: docRef.id, 
      ...sanitizedData 
    };
  } catch (error) {
    console.error('Erro ao adicionar categoria:', error);
    throw error;
  }
};

/**
 * Atualiza uma categoria existente
 * @param {string} salonId - ID do salão
 * @param {string} categoryId - ID da categoria
 * @param {object} categoryData - Novos dados da categoria
 * @returns {Promise<object>} - Categoria atualizada
 */
export const updateCategory = async (salonId, categoryId, categoryData) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!categoryId) {
    throw new Error('ID da categoria é obrigatório');
  }
  
  if (!categoryData || !categoryData.nome) {
    throw new Error('Nome da categoria é obrigatório');
  }
  
  try {
    // Verificar se o documento existe
    const categoryRef = firestoreDoc(db, `salons/${salonId}/categories/${categoryId}`);
    const categorySnap = await getDoc(categoryRef);
    
    if (!categorySnap.exists()) {
      throw new Error(`Categoria não encontrada: ${categoryId}`);
    }
    
    const sanitizedData = {
      ...categoryData,
      updatedAt: new Date()
    };
    
    await updateDoc(categoryRef, sanitizedData);
    return { 
      id: categoryId, 
      ...sanitizedData 
    };
  } catch (error) {
    console.error('Erro ao atualizar categoria:', error);
    throw error;
  }
};

/**
 * Remove uma categoria
 * @param {string} salonId - ID do salão
 * @param {string} categoryId - ID da categoria
 * @returns {Promise<boolean>} - True se removido com sucesso
 */
export const deleteCategory = async (salonId, categoryId) => {
  if (!salonId) {
    throw new Error('ID do salão é obrigatório');
  }
  
  if (!categoryId) {
    throw new Error('ID da categoria é obrigatório');
  }
  
  try {
    // Verificar se o documento existe
    const categoryRef = firestoreDoc(db, `salons/${salonId}/categories/${categoryId}`);
    const categorySnap = await getDoc(categoryRef);
    
    if (!categorySnap.exists()) {
      throw new Error(`Categoria não encontrada: ${categoryId}`);
    }
    
    await deleteDoc(categoryRef);
    
    // Aqui você pode adicionar código para atualizar os serviços que usavam esta categoria,
    // movendo-os para outra categoria ou marcando-os como "sem categoria"
    
    return true;
  } catch (error) {
    console.error('Erro ao excluir categoria:', error);
    throw error;
  }
};

/**
 * Cria as associações entre profissionais e serviços
 * @param {string} salonId - ID do salão
 * @param {string} professionalId - ID do profissional 
 * @param {Array} serviceIds - Lista de IDs dos serviços que o profissional pode executar
 * @returns {Promise<void>}
 */
export const createProfessionalServiceAssociations = async (salonId, professionalId, serviceIds) => {
  if (!salonId || !professionalId) {
    console.log("[createProfessionalServiceAssociations] Dados inválidos - salonId ou professionalId ausentes:", { salonId, professionalId });
    return;
  }
  
  if (!serviceIds || !Array.isArray(serviceIds) || serviceIds.length === 0) {
    console.log("[createProfessionalServiceAssociations] Nenhum serviço para associar:", { salonId, professionalId, serviceIds });
    return;
  }
  
  try {
    console.log("[createProfessionalServiceAssociations] Iniciando associações:", { 
      salonId, 
      professionalId, 
      serviceIdsCount: serviceIds.length 
    });
    
    // Importamos a função batchUpdateProfessionalServiceAssociations diretamente aqui
    // para evitar problemas de dependência circular
    const { batchUpdateProfessionalServiceAssociations, getService } = await import('./professionalServices');
    
    // Obter detalhes de cada serviço para incluir metadados nos objetos de associação
    const serviceAssociations = [];
    
    for (const serviceId of serviceIds) {
      try {
        // Criar objetos de associação com dados de metadados
        serviceAssociations.push({
          serviceId, 
          // Por padrão, não temos customPrice ou customDuration no fluxo de signup
          // Esses valores podem ser adicionados posteriormente se necessário
          customPrice: null,
          customDuration: null,
          isActive: true
        });
      } catch (err) {
        console.warn(`[createProfessionalServiceAssociations] Erro ao processar serviço ID ${serviceId}:`, err);
      }
    }
    
    console.log("[createProfessionalServiceAssociations] Associações preparadas:", {
      count: serviceAssociations.length,
      sample: serviceAssociations.length > 0 ? serviceAssociations[0] : null
    });
    
    // Usar a função da nova arquitetura para criar as associações na coleção professional_services
    if (serviceAssociations.length > 0) {
      console.log("[createProfessionalServiceAssociations] Chamando batchUpdateProfessionalServiceAssociations com:", {
        count: serviceAssociations.length
      });
      
      const result = await batchUpdateProfessionalServiceAssociations(salonId, professionalId, serviceAssociations);
      
      console.log(`[createProfessionalServiceAssociations] Associações criadas com sucesso:`, { result });
      return result;
    } else {
      console.log("[createProfessionalServiceAssociations] Nenhuma associação válida para criar");
      return false;
    }
  } catch (error) {
    console.error('[createProfessionalServiceAssociations] Erro ao criar associações:', error);
    throw error;
  }
};

/**
 * Cria um novo perfil de salão no Firestore com subcoleções para serviços e profissionais
 * @param {object} salonData - Dados do salão para criar
 * @returns {Promise<string>} - ID do salão criado
 */
export const createSalonProfile = async (salonData) => {
  try {
    // Extrair os dados que serão armazenados em subcoleções
    const { categories, professionals, ...mainSalonData } = salonData;
    
    console.log("[createSalonProfile] Dados recebidos:", { 
      mainData: Object.keys(mainSalonData),
      categoriesCount: categories?.length || 0,
      professionalsCount: professionals?.length || 0
    });
    
    // Gerar um ID baseado no nome do salão (slug)
    const salonId = salonData.name
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]+/g, '')
      .replace(/\-\-+/g, '-')
      .substring(0, 30);
    
    // Verificar se já existe um salão com este ID
    const docRef = firestoreDoc(db, 'salons', salonId);
    const docSnap = await getDoc(docRef);
    
    // Definir o ID final do salão
    let finalSalonId = salonId;
    
    if (docSnap.exists()) {
      // Se já existe, adiciona um número aleatório ao ID
      const randomSuffix = Math.floor(Math.random() * 1000);
      finalSalonId = `${salonId}-${randomSuffix}`;
      
      // Criar o documento com o ID único
      const uniqueDocRef = firestoreDoc(db, 'salons', finalSalonId);
      await setDoc(uniqueDocRef, {
        id: finalSalonId,
        ...mainSalonData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    } else {
      // Se não existe, cria normalmente
      await setDoc(docRef, {
        id: finalSalonId,
        ...mainSalonData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
    }
    
    // Mapeamento de categorias e serviços para armazenar IDs
    const serviceMap = {};
    const categoryMap = {};
    
    // Criar coleção de categorias
    if (categories && categories.length > 0) {
      const categoriesRef = collection(db, `salons/${finalSalonId}/categories`);
      
      for (let i = 0; i < categories.length; i++) {
        const category = categories[i];
        const categoryDoc = await addDoc(categoriesRef, {
          nome: category.name,
          ordem: i,
          createdAt: new Date()
        });
        
        // Armazenar o ID da categoria no mapa
        categoryMap[category.name] = categoryDoc.id;
        
        // Adicionar serviços para cada categoria usando a estrutura plana
        if (category.services && category.services.length > 0) {
          for (let j = 0; j < category.services.length; j++) {
            const service = category.services[j];
            
            // Usar a função addService do módulo de serviços para manter consistência
            const serviceData = {
              nome: service.name,
              preco: Number(service.price) || 0,
              duracao: Number(service.duration) || 30,
              descricao: service.description || '',
              categoria: categoryDoc.id,
              ordem: j,
              setupTime: 0,
              finishTime: 0,
              allowsOverlap: false
            };
            
            const newService = await addService(finalSalonId, serviceData);
            
            // Armazenar o ID do serviço para associação com profissionais
            const serviceKey = `${category.name}-${service.name}`;
            serviceMap[serviceKey] = newService.id;
          }
        }
      }
    }
    
    // Criar coleção de profissionais e associar aos serviços
    if (professionals && Array.isArray(professionals) && professionals.length > 0) {
      const professionalsRef = collection(db, `salons/${finalSalonId}/professionals`);
      
      console.log("[createSalonProfile] Processando profissionais:", professionals.length);
      
      for (let i = 0; i < professionals.length; i++) {
        const professional = professionals[i];
        // Removemos os dados temporários que não precisam ir para o banco
        const { photoURL, photo, serviceIds = [], ...profData } = professional;
        
        console.log(`[createSalonProfile] Criando profissional ${i+1}/${professionals.length}:`, {
          nome: profData.name,
          serviceIds: serviceIds.length
        });
        
        // Criar o documento do profissional
        const professionalDoc = await addDoc(professionalsRef, {
          ...profData,
          nome: profData.name,  // Garantir que o nome seja armazenado no campo 'nome'
          serviceCount: 0,      // Inicializar serviceCount como 0
          ordem: i,
          createdAt: new Date(),
          updatedAt: new Date()
        });
        
        console.log(`[createSalonProfile] Profissional criado com ID: ${professionalDoc.id}`);
        
        // Associar profissional aos serviços
        if (serviceIds && Array.isArray(serviceIds) && serviceIds.length > 0) {
          // Converter IDs de serviço do formato exibido (categoryName-serviceName) para IDs reais do Firestore
          const actualServiceIds = serviceIds
            .filter(id => serviceMap[id])
            .map(id => serviceMap[id]);
            
          console.log(`[createSalonProfile] Associando ${actualServiceIds.length} serviços ao profissional ${professionalDoc.id}`);
            
          // Criar as associações
          if (actualServiceIds.length > 0) {
            try {
              await createProfessionalServiceAssociations(
                finalSalonId, 
                professionalDoc.id, 
                actualServiceIds
              );
              console.log(`[createSalonProfile] Associações criadas com sucesso`);
            } catch (error) {
              console.error(`[createSalonProfile] Erro ao criar associações:`, error);
            }
          }
        }
      }
    } else {
      console.log("[createSalonProfile] Nenhum profissional para processar ou array inválido:", professionals);
    }
    
    return finalSalonId;
  } catch (error) {
    console.error('Erro ao criar perfil do salão:', error);
    throw error;
  }
};

export const updateAppointment = async (salonId, appointmentId, updates) => {
  try {
    // Verificar quais campos estão sendo atualizados para otimizar a operação
    const updateData = {};
    
    // Campos comuns que podem ser atualizados
    const validFields = [
      'data', 'horario', 'cliente', 'whatsapp', 'email', 
      'profissional', 'servicos', 'servicosDetalhes', 'valorTotal', 'observacoes',
      'cancelado', 'confirmado', 'pago', 'enviouLembrete',
      'profissionalConfirmado', 'profissionalConfirmadoId', 'valorCustomizado'
    ];
    
    validFields.forEach(field => {
      if (updates[field] !== undefined) {
        updateData[field] = updates[field];
      }
    });
    
    const docRef = firestoreDoc(db, 'salons', salonId, 'appointments', appointmentId);
    await updateDoc(docRef, updateData);
    
    return true;
  } catch (error) {
    console.error('Erro ao atualizar agendamento:', error);
    throw error;
  }
}; 